import * as React from 'react';
import styled from 'styled-components';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { RegisterV2Model } from '../../../../models/AccountModels';
import Input from '../../../../commonComponents/appForm/Input';
import EmailField from '../../../../commonComponents/appForm/EmailField';
import AppForm from '../../common/AppForm';
import { superLightGreyColour, tabletPortraitMinWidth } from '../../../cssConstants';
import PageTitle from '../../common/PageTitle';
import ExternalLink from '../../../common/ExternalLink';
import { getRegistrationDataFromLocalStorage, setRegistrationDataToLocalStorage } from '../../../../core/localStorage';
import { isEqualIgnoreCase } from '../../../../lib/utils';
import { getPrivacyPolicyLink, getTermsOfBusinessLink } from '../../../../core/countryBasedConfig';
import { hasValue } from '../../../../lib/lib';
import getAppVariantConfig from '../../../../appVariants/appVariantConfig';
import { allowedEmailDomainsForBetterOffApp } from '../../../../core/constants';
import { isBetterOffApp } from '../../../../core/appType';

const RegisterForm = ({ onSubmit }: Props) => {
	const { registerPageSubtitle } = getAppVariantConfig();
	const methods = useForm();
	const { reset, control } = methods;
	const email = useWatch({ control, name: 'email', defaultValue: null });

	useEffect(() => {
		const storedData = getRegistrationDataFromLocalStorage();
		if (storedData) {
			const parsedData: RegisterV2Model = JSON.parse(storedData);
			reset(parsedData);
		}
	}, []);

	const handleSubmit = async (registerModel: RegisterV2Model) => {
		setRegistrationDataToLocalStorage(JSON.stringify(registerModel));
		onSubmit();
	};

	const isValidDomain = (emailValue: string): boolean => {
		if (!emailValue) return false;

		const atIndex = emailValue.lastIndexOf('@');

		if (atIndex === -1 || atIndex === emailValue.length - 1) {
			return false;
		}

		const emailDomain = emailValue.slice(atIndex + 1).toLowerCase();

		return allowedEmailDomainsForBetterOffApp.some((allowedDomain) => emailDomain === allowedDomain.toLowerCase());
	};

	return (
		<>
			<PageTitle title="Enter your details" subtitle={registerPageSubtitle} />
			<AppForm methods={methods} onSubmit={handleSubmit} primaryButtonText="Continue" singleColumn>
				<Input
					label="First name"
					name="firstName"
					maxLength={50}
					validate={(value: string) => hasValue(value) || 'Please enter your first name.'}
				/>
				<Input
					label="Last name"
					name="lastName"
					maxLength={50}
					validate={(value: string) => hasValue(value) || 'Please enter your last name.'}
				/>
				<EmailField
					label="Email address"
					validate={(value: string) => {
						if (!hasValue(value)) {
							return 'Please enter your email address.';
						}
						if (isBetterOffApp() && !isValidDomain(value)) {
							return 'Please enter your company email.';
						}
						return true;
					}}
				/>
				<EmailField
					label="Confirm your email address"
					name="confirmEmail"
					validate={(value: string) =>
						(hasValue(value) && isEqualIgnoreCase(value, email)) || "The email addresses don't match."
					}
					onPaste={(e: React.ClipboardEvent) => e.preventDefault()}
				/>
				<Terms>
					By confirming below, I agree to the Kelly+Partners{' '}
					<ExternalLink href={getTermsOfBusinessLink()}>Terms of Service</ExternalLink> and{' '}
					<ExternalLink href={getPrivacyPolicyLink()}>Privacy Policy</ExternalLink>, which explain how services are
					provided and how personal information is handled.
				</Terms>
			</AppForm>
		</>
	);
};

export default RegisterForm;

interface Props {
	onSubmit?: () => void;
}

export const Terms = styled.p`
	font-size: 12px;
	font-weight: 400;
	line-height: 16px;
	margin-top: 5px;
	color: ${superLightGreyColour};
	letter-spacing: 0.4px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 12px;
	}
`;
